import type { Logger } from '@/types/Logger';
import type { MissingFileKeyHelpRequest, FileNeedingNewKey } from '@/types/fileKey/FileKeyFacilitation';
import type { HeliaNode } from '@/types/poc/helia';
import type { PocServices } from '@/types/services.types';
import type { AppDispatch } from '@/store';
import { setDecapsKey } from '@/store/slices/auth/authSlice';
import {
  facilitationComplete,
  fileKeyFailed,
  fileKeyProcessed,
  startFacilitation,
} from '@/store/slices/fileKey/fileKeyFacilitationSlice';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { announceFileKeyReadyThunk } from '@/store/thunks/poc/messaging/announceFileKeyReadyThunk';
import { bytesToNumbers, hexToBytes, numbersToBytes, parseJsonArrayToBytes } from '@/utils/hex.utils';

const HEX_BASE = 16;
const HEX_PAD_LENGTH = 2;
const ENCRYPTION_FETCH_TIMEOUT_MS = 30_000;

interface HeliaFileKeyBlob {
  cipherText: number[];
  encryptedFileKey: {
    encryptedData: number[];
    iv: number[];
    authTag: number[];
  };
}

interface FacilitationContext {
  facilitatorPrivateKey: Uint8Array;
  currentUserId: string;
  dispatch: AppDispatch;
  pocServices: PocServices;
  log: Logger;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNumberArray(value: unknown): value is number[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'number');
}

function isMissingFileKeyHelpRequestArray(value: unknown): value is MissingFileKeyHelpRequest[] {
  if (!Array.isArray(value)) {
    return false;
  }

  return value.every((entry) => {
    if (!isRecord(entry)) {
      return false;
    }

    const requestor = entry.requestor;
    return (
      isRecord(requestor) &&
      typeof requestor.userId === 'string' &&
      typeof requestor.encapsKey === 'string' &&
      Array.isArray(entry.filesNeedingNewFileKey)
    );
  });
}

function parseHeliaFileKeyBlob(value: string): HeliaFileKeyBlob {
  const parsed: unknown = JSON.parse(value);
  if (!isRecord(parsed) || !isNumberArray(parsed.cipherText) || !isRecord(parsed.encryptedFileKey)) {
    throw new Error('Invalid file key blob shape');
  }

  const encryptedFileKey = parsed.encryptedFileKey;
  if (
    !isNumberArray(encryptedFileKey.encryptedData) ||
    !isNumberArray(encryptedFileKey.iv) ||
    !isNumberArray(encryptedFileKey.authTag)
  ) {
    throw new Error('Invalid encrypted file key blob shape');
  }

  return {
    cipherText: parsed.cipherText,
    encryptedFileKey: {
      encryptedData: encryptedFileKey.encryptedData,
      iv: encryptedFileKey.iv,
      authTag: encryptedFileKey.authTag,
    },
  };
}

export const facilitateFileKeys = createAppAsyncThunk(
  'fileKeyFacilitation/facilitate',
  async(_, { dispatch, getState, extra, rejectWithValue }) => {
    const {
      pocServices,
      userService,
      fileKeyFacilitationService,
      loggerService,
    } = extra;
    const log = loggerService.createLogger({ namespace: 'six3ro:file-key-facilitation' });

    try {
      log.info('Checking for missing file key requests...');
      const rawRequests = await fileKeyFacilitationService.getMissingFileKeyRequests();
      log.debug('Missing file key response received', rawRequests);

      if (!isMissingFileKeyHelpRequestArray(rawRequests)) {
        log.error('Missing file key response has unexpected shape', rawRequests);
        throw new Error('Invalid missing file key response shape');
      }

      const requests = rawRequests;
      if (requests.length === 0) {
        log.info('No missing file key requests. Nothing to facilitate.');
        return { processed: 0, total: 0 };
      }

      const totalFiles = requests.reduce((sum, req) => sum + req.filesNeedingNewFileKey.length, 0);
      log.info(`Found ${requests.length} requestor(s), ${totalFiles} file(s) to facilitate.`);
      dispatch(startFacilitation({ totalCount: totalFiles }));

      const state = getState();
      const currentUserId = state.user.user?.id;
      if (currentUserId == null) {
        throw new Error('User not authenticated');
      }

      let facilitatorPrivateKey: Uint8Array | null = null;
      if (state.auth.decapsKey.length > 0) {
        log.debug('Using cached private key');
        facilitatorPrivateKey = hexToBytes(state.auth.decapsKey);
      } else {
        log.debug('No cached key, fetching and decrypting...');
        const ukek = state.auth.ukek;
        if (ukek.length === 0) {
          throw new Error('UKEK not available for private key decryption');
        }

        const { decapsKey: encryptedPrivateKeyHex } = await userService.getDecapsKey(currentUserId);
        facilitatorPrivateKey = await pocServices.cryptoService.decryptPrivateKey(
          encryptedPrivateKeyHex,
          hexToBytes(ukek)
        );

        dispatch(setDecapsKey(Array.from(facilitatorPrivateKey)
          .map((byte) => byte.toString(HEX_BASE).padStart(HEX_PAD_LENGTH, '0'))
          .join('')));
        log.debug('Private key decrypted and cached');
      }

      const ctx: FacilitationContext = {
        facilitatorPrivateKey,
        currentUserId,
        dispatch,
        pocServices,
        log,
      };

      for (const request of requests) {
        await processRequestor(request, ctx);
      }

      dispatch(facilitationComplete());
      const finalState = getState();
      const result = {
        processed: finalState.fileKeyFacilitation.processedCount,
        total: totalFiles,
        errors: finalState.fileKeyFacilitation.errors,
      };
      log.info('File key facilitation complete', result);
      return result;
    } catch (error) {
      log.error('File key facilitation failed', error);
      dispatch(facilitationComplete());
      return rejectWithValue({
        message: error instanceof Error ? error.message : 'Unknown facilitation error',
      });
    }
  }
);

async function processRequestor(
  request: MissingFileKeyHelpRequest,
  ctx: FacilitationContext
): Promise<void> {
  ctx.log.debug(
    `Processing ${request.filesNeedingNewFileKey.length} file(s) for requestor: ${request.requestor.userId}`
  );
  const recipientPublicKey = parseJsonArrayToBytes(request.requestor.encapsKey);

  for (const file of request.filesNeedingNewFileKey) {
    try {
      await processOneFileKey(file, recipientPublicKey, request.requestor.userId, ctx);
      ctx.dispatch(fileKeyProcessed());
    } catch (error) {
      const message = `Failed for file ${file.fileId} (six: ${file.sixId}): ${error instanceof Error ? error.message : 'Unknown error'}`;
      ctx.log.error(message);
      ctx.dispatch(fileKeyFailed(message));
    }
  }
}

async function processOneFileKey(
  file: FileNeedingNewKey,
  recipientPublicKey: Uint8Array,
  requestorUserId: string,
  ctx: FacilitationContext
): Promise<void> {
  const { facilitatorPrivateKey, currentUserId, pocServices, log } = ctx;
  const { p2pStackManagerService, fileEncryptionService, fileStoreService } = pocServices;

  const helia: HeliaNode | null = p2pStackManagerService.getHeliaNode(file.sixId);
  if (helia === null) {
    throw new Error(`Helia node not initialized for six: ${file.sixId}`);
  }

  log.debug(`Fetching facilitator file key CID: ${file.facilitatorFileKeyCID}`);
  const encryptionJsonBytes = await fileStoreService.getFile(helia, file.facilitatorFileKeyCID, {
    signal: AbortSignal.timeout(ENCRYPTION_FETCH_TIMEOUT_MS),
  });
  const blob = parseHeliaFileKeyBlob(new TextDecoder().decode(new Uint8Array(encryptionJsonBytes)));

  const facilitatorEncryption = {
    recipientId: currentUserId,
    cipherText: numbersToBytes(blob.cipherText),
    encryptedFileKey: {
      encryptedData: numbersToBytes(blob.encryptedFileKey.encryptedData),
      iv: numbersToBytes(blob.encryptedFileKey.iv),
      authTag: numbersToBytes(blob.encryptedFileKey.authTag),
    },
  };

  const reEncrypted = await fileEncryptionService.reEncryptFileKeyForRecipient(
    facilitatorEncryption,
    facilitatorPrivateKey,
    recipientPublicKey
  );
  log.debug(`Re-encrypted file key for requestor: ${requestorUserId}, file: ${file.fileId}`);

  const newBlob: HeliaFileKeyBlob = {
    cipherText: bytesToNumbers(reEncrypted.cipherText),
    encryptedFileKey: {
      encryptedData: bytesToNumbers(reEncrypted.encryptedFileKey.encryptedData),
      iv: bytesToNumbers(reEncrypted.encryptedFileKey.iv),
      authTag: bytesToNumbers(reEncrypted.encryptedFileKey.authTag),
    },
  };
  const blobBytes = new TextEncoder().encode(JSON.stringify(newBlob));
  const blobFile = new File([blobBytes], `filekey-${file.fileId}-${requestorUserId}.json`, {
    type: 'application/octet-stream',
  });

  const addResult = await fileStoreService.addFile(helia, blobFile);
  const newFileKeyCID = addResult.cid.toString();
  log.debug(`Stored new file key in Helia, CID: ${newFileKeyCID}`);
  const metadata = {
    id: file.fileId,
    cid: newFileKeyCID,
    name: `filekey-${file.fileId}`,
    size: blobBytes.byteLength,
    type: 'application/octet-stream',
    addedBy: currentUserId,
    lastModified: Date.now(),
    sixId: file.sixId,
    path: '',
    uploadedByUserName: currentUserId,
    uploadedByUserId: currentUserId,
  };

  await ctx.dispatch(announceFileKeyReadyThunk({
    sixId: file.sixId,
    fileId: file.fileId,
    newFileKeyCID,
    newFileKeyForUserId: requestorUserId,
    newFileKeyCreatorUserId: currentUserId,
    metadata,
  })).unwrap();

  log.info(`Published file-key-ready-for-download for file: ${file.fileId}, requestor: ${requestorUserId}, CID: ${newFileKeyCID}`);
}
